import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, Modal, Popconfirm } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
import { useRestoreBankMutation } from "../../../redux/banks/bankAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// CUSTOM HOOKS
import useDeletedBanks from "../../../customHooks/GET_requests/useDeletedBanks";
// COMPONENTS
import DeletedBanksTable from "../../edgeManagement/banks/DeletedBanksTable";
import RestorePortField from "../../edgeManagement/banks/RestorePortField";
import { WizardOpGate } from "../WizardStepGate";
// CONSTANTS
import { isLobbyVision2, logicalPortOutOfRange } from "../../../constants/bankLogicalPort";
// UTILS
import {
  DeletedBankRow,
  RestoreBankResult,
  clampLogicalPort,
  notifyBankRestored,
  toRestoreBankResult,
} from "../../../utils/deletedBanks";
import { describeError } from "../utils/describeError";
// INTERFACES
import { RootState } from "../../../app/store";

const PANEL_KEY = "deleted-banks";

export interface DeletedBanksPanelProps {
  orgId: string;
  /** The server whose banks the step is editing; keeps the panel in step with its table. */
  serverId?: string;
  channelId?: string;
  /** The upper-case protocol of an adapter, "" where the step does not know it. */
  resolveProtocol: (adapterId: string) => string;
  /**
   * Called once a restore has succeeded. The step re-opens everything that
   * depends on banks and reloads its own list of them.
   */
  onRestored: (result: RestoreBankResult, row: DeletedBankRow) => void | Promise<void>;
  /** Called with the server's message when a restore is refused. */
  onError: (message: string) => void;
}

interface PortCorrection {
  row: DeletedBankRow;
  protocol: string;
}

interface BodyProps extends DeletedBanksPanelProps {
  canRestore: boolean;
}

function DeletedBanksPanelBody({
  orgId,
  serverId = undefined,
  channelId = undefined,
  resolveProtocol,
  onRestored,
  onError,
  canRestore,
}: BodyProps) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);

  const [expanded, setExpanded] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [correction, setCorrection] = useState<PortCorrection | null>(null);
  const [correctedPort, setCorrectedPort] = useState<number | null>(null);

  const { rows, total, loaded, loading, error, page, setPage } = useDeletedBanks(
    { orgId, serverId, channelId },
    expanded,
  );
  const [triggerRestoreBank] = useRestoreBankMutation();

  // Restores `row`, sending `port` only when the operator corrected it. A bank
  // restored as it was carries no body at all.
  const restore = async (row: DeletedBankRow, port?: number) => {
    setRestoringId(row.id);
    let result: RestoreBankResult;
    try {
      const token = await GETJwtToken();
      const answer = await triggerRestoreBank({
        id: row.id,
        body: port === undefined ? undefined : { logical_port: port },
        token,
      }).unwrap();
      result = toRestoreBankResult(answer);
    } catch (err) {
      // Closed so the step's error banner is not hidden behind the modal's mask.
      setCorrection(null);
      onError(describeError(err));
      return;
    } finally {
      setRestoringId(null);
    }
    setCorrection(null);
    notifyBankRestored(
      t("edgeManagement.banks.deleted.restoredTitle", { name: row.name }),
      result,
      theme,
    );
    await onRestored(result, row);
  };

  const openCorrection = (row: DeletedBankRow, protocol: string) => {
    setCorrectedPort(clampLogicalPort(protocol, row.logicalPort));
    setCorrection({ row, protocol });
  };

  const renderAction = (row: DeletedBankRow) => {
    const protocol = resolveProtocol(row.adapterId);
    const busy = restoringId === row.id;
    // LobbyVision2 refuses a port outside 0..255, so restoring this bank as it
    // was would only earn a 400. Ask for a port in range up front instead.
    if (isLobbyVision2(protocol) && logicalPortOutOfRange(protocol, row.logicalPort)) {
      return (
        <WizardOpGate op="delete">
          <Button
            type="link"
            size="small"
            loading={busy}
            onClick={() => openCorrection(row, protocol)}
          >
            {t("edgeManagement.banks.deleted.restoreWithPort")}
          </Button>
        </WizardOpGate>
      );
    }
    // Restoring undoes a delete, so it is gated by the page's delete permission.
    return (
      <WizardOpGate op="delete">
        <Popconfirm
          title={t("edgeManagement.banks.deleted.confirm", { name: row.name })}
          okText={t("edgeManagement.banks.deleted.restore")}
          cancelText={t("edgeManagement.banks.deleted.cancel")}
          onConfirm={() => restore(row)}
        >
          <Button type="link" size="small" loading={busy}>
            {t("edgeManagement.banks.deleted.restore")}
          </Button>
        </Popconfirm>
      </WizardOpGate>
    );
  };

  const title = t("edgeManagement.banks.deleted.title");

  return (
    <div data-testid="deleted-banks-panel" style={{ marginTop: 16 }}>
      <Collapse
        activeKey={expanded ? [PANEL_KEY] : []}
        onChange={(keys) => setExpanded(Array.isArray(keys) ? keys.length > 0 : Boolean(keys))}
      >
        <Collapse.Panel key={PANEL_KEY} header={loaded ? `${title} (${total})` : title}>
          <DeletedBanksTable
            rows={rows}
            total={total}
            page={page}
            loading={loading}
            error={error}
            onPageChange={setPage}
            renderAction={canRestore ? renderAction : undefined}
          />
        </Collapse.Panel>
      </Collapse>
      <Modal
        open={correction !== null}
        title={t("edgeManagement.banks.deleted.portTitle")}
        okText={t("edgeManagement.banks.deleted.restore")}
        cancelText={t("edgeManagement.banks.deleted.cancel")}
        okButtonProps={{
          disabled:
            correction === null ||
            correctedPort === null ||
            logicalPortOutOfRange(correction.protocol, correctedPort),
        }}
        confirmLoading={correction !== null && restoringId === correction.row.id}
        onOk={() => {
          if (correction !== null && correctedPort !== null) restore(correction.row, correctedPort);
        }}
        onCancel={() => setCorrection(null)}
        destroyOnClose
      >
        {correction !== null && (
          <>
            <p>
              {t("edgeManagement.banks.deleted.portExplain", {
                name: correction.row.name,
                port: correction.row.logicalPort,
              })}
            </p>
            <RestorePortField
              protocolUpper={correction.protocol}
              value={correctedPort}
              onChange={setCorrectedPort}
            />
          </>
        )}
      </Modal>
    </div>
  );
}

/**
 * The wizard Banks step's "Deleted banks" panel: a collapsed section listing the
 * banks that were deleted from this organization, each with a Restore action.
 *
 * Rendered only for a user who holds `banks-deleted-view`; the Restore action
 * needs `banks-restore` on top. Nothing is requested until the section is
 * expanded.
 */
function DeletedBanksPanel({
  orgId,
  serverId = undefined,
  channelId = undefined,
  resolveProtocol,
  onRestored,
  onError,
}: DeletedBanksPanelProps) {
  // `user` is read defensively: the step is mounted on its own in unit tests
  // whose store carries no user slice, and no slice means no permission.
  const permissionSet = useSelector((state: RootState) => state.user?.permissionSet);
  const granted = permissionSet ?? [];

  if (!orgId || !hasPermission(Permission.BanksDeletedView, granted)) return null;

  return (
    <DeletedBanksPanelBody
      orgId={orgId}
      serverId={serverId}
      channelId={channelId}
      resolveProtocol={resolveProtocol}
      onRestored={onRestored}
      onError={onError}
      canRestore={hasPermission(Permission.BanksRestore, granted)}
    />
  );
}

export default DeletedBanksPanel;
